'use client';

import { useState } from 'react';
import { Box, Typography, Grid, Alert } from '@mui/material';
import FileUpload from '../components/FileUpload';
import STLViewer from 'components/viewer/STLViewer';

interface ModelUploadStepProps {
  modelUrl: string | undefined;
  onModelUpload: (file: File) => void;
}

export default function ModelUploadStep({ modelUrl, onModelUpload }: ModelUploadStepProps) {
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileSelect = async (file: File) => {
    try {
      // Create a temporary URL for the uploaded file
      const objectUrl = URL.createObjectURL(file);
      onModelUpload(file);
      setUploadError(null);
    } catch (error) {
      setUploadError('Error processing the file. Please try again.');
      console.error('Error processing file:', error);
    }
  };

  return (
    <div>
      <Typography variant="h5" gutterBottom>
        Upload 3D Model
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Upload your STL file to help makers understand your requirements better. The model will be
        visible to makers who view your job posting.
      </Typography>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <FileUpload
            onFileSelect={handleFileSelect}
            accept={{
              'model/stl': ['.stl'],
              'application/octet-stream': ['.stl'],
            }}
            maxSize={50 * 1024 * 1024} // 50MB
          />
          {uploadError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {uploadError}
            </Alert>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {modelUrl ? (
            <Box
              sx={{
                height: 400,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <STLViewer url={modelUrl} height={400} />
            </Box>
          ) : (
            <Box
              sx={{
                height: 400,
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'background.paper',
              }}
            >
              <Typography variant="body1" color="text.secondary">
                Upload a model to preview it here
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  );
}
